import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAcquireIdToken } from 'src/hooks/auth/useAcquireIdToken';
import { invalidateNotification } from 'src/hooks/notifications/invalidateNotification';
import type { components } from 'src/infrastructure/rest-api/generated';
import { recordError } from 'src/infrastructure/telemetry';

type NotificationPayload = components['schemas']['NotificationPayload'];

export const useNotificationHub = (): void => {
    const queryClient = useQueryClient();
    const acquireIdToken = useAcquireIdToken();

    useEffect(() => {
        const connection = new HubConnectionBuilder()
            .withUrl('/api/hubs', {
                transport: HttpTransportType.LongPolling,
                accessTokenFactory: async () => (await acquireIdToken()) ?? '',
            })
            .withAutomaticReconnect()
            .build();
        let disposed = false;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        connection.on('Notify', (payload: NotificationPayload) => invalidateNotification(queryClient, payload));
        connection.onreconnected(() => {
            if (disposed) return;
            void queryClient.invalidateQueries({ queryKey: ['/api/announcements'] });
            void queryClient.invalidateQueries({ queryKey: ['/api/pipelinestatus'] });
        });

        const start = async (): Promise<void> => {
            try {
                await connection.start();
                if (disposed) {
                    await connection.stop();
                } else {
                    void queryClient.invalidateQueries({ queryKey: ['/api/announcements'] });
                    void queryClient.invalidateQueries({ queryKey: ['/api/pipelinestatus'] });
                }
            } catch (error) {
                if (!disposed) {
                    recordError(error, { apiRoute: '/api/hubs' });
                    retryTimer = setTimeout(() => void start(), 10_000);
                }
            }
        };

        connection.onclose(() => {
            if (!disposed) retryTimer = setTimeout(() => void start(), 10_000);
        });
        void start();

        return () => {
            disposed = true;
            clearTimeout(retryTimer);
            void connection.stop();
        };
    }, [acquireIdToken, queryClient]);
};
